/**
 * ContentCaptionSyncDemo — Preview composition para o Studio
 *
 * Demonstra os 5 presets do ContentCaptionSync com dados Stack B (word-level).
 * 30fps · 1080×1920 · 10s por preset = 50s total (1500 frames)
 *
 * Uso: renderizar no Studio para validar visual antes de integrar
 * em compositions de produção.
 */
import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ContentCaptionSync, WordTimestamp } from "../components/overlays/ContentCaptionSync";

// ── Sample words (Stack B format) ─────────────────────────────────────────────
const SAMPLE_WORDS: WordTimestamp[] = [
  { word: "seu",      start: 0.00, end: 0.18 },
  { word: "preço",    start: 0.19, end: 0.45, emphasis: true },
  { word: "mínimo",   start: 0.46, end: 0.80 },
  { word: "não",      start: 0.82, end: 0.95 },
  { word: "é",        start: 0.96, end: 1.05 },
  { word: "o",        start: 1.06, end: 1.14 },
  { word: "que",      start: 1.15, end: 1.25 },
  { word: "você",     start: 1.26, end: 1.45 },
  { word: "acha",     start: 1.46, end: 1.72 },
  { word: "é",        start: 1.73, end: 1.85 },
  { word: "o",        start: 1.86, end: 1.94 },
  { word: "que",      start: 1.95, end: 2.10 },
  { word: "o",        start: 2.11, end: 2.19 },
  { word: "cliente",  start: 2.20, end: 2.55, emphasis: true },
  { word: "aceita",   start: 2.56, end: 2.90 },
  { word: "pagar",    start: 2.91, end: 3.30 },
];

// 5 presets × 10s = 50s total; 10s = 300 frames por preset
const FRAMES_PER_PRESET = 300;

type CaptionPreset =
  | "bold-center-pop"
  | "editorial-dimus"
  | "neon-glow-flicker"
  | "humane-statement"
  | "coolvetica-friendly";

const PRESETS: CaptionPreset[] = [
  "bold-center-pop",
  "editorial-dimus",
  "neon-glow-flicker",
  "humane-statement",
  "coolvetica-friendly",
];

const PRESET_LABELS: Record<CaptionPreset, string> = {
  "bold-center-pop": "bold-center-pop",
  "editorial-dimus": "editorial-dimus",
  "neon-glow-flicker": "neon-glow-flicker",
  "humane-statement": "humane-statement",
  "coolvetica-friendly": "coolvetica-friendly",
};

export const CAPTION_DEMO_TOTAL = FRAMES_PER_PRESET * PRESETS.length;

export const ContentCaptionSyncDemo: React.FC = () => {
  const frame = useCurrentFrame();

  const presetIdx = Math.min(
    Math.floor(frame / FRAMES_PER_PRESET),
    PRESETS.length - 1
  );
  const currentPreset = PRESETS[presetIdx];
  const startFrame = presetIdx * FRAMES_PER_PRESET;

  // Offset words so they restart for each preset
  const offsetWords: WordTimestamp[] = SAMPLE_WORDS.map((w) => ({
    ...w,
    start: w.start,
    end: w.end,
  }));

  return (
    <div style={{ width: 1080, height: 1920, background: "#0d0d0d", position: "relative" }}>
      {/* Background gradient */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at 50% 40%, rgba(0,223,130,0.08) 0%, transparent 60%)",
        }}
      />

      {/* Preset label */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
          fontSize: 36,
          color: "rgba(255,255,255,0.4)",
          letterSpacing: "0.05em",
        }}
      >
        {PRESET_LABELS[currentPreset]}
      </div>

      {/* Caption overlay */}
      <ContentCaptionSync
        words={offsetWords}
        preset={currentPreset}
        position="center"
        startFrame={startFrame}
        wordsPerLine={4}
      />

      {/* Debug safe zone toggle — sempre off em produção */}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
